import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Box, ThemeProvider } from "@mui/material";
import { Toaster } from "react-hot-toast";
// import { useLocation } from "react-router-dom";
import useTheme from "hooks/useTheme.hook";
import { createDynamicTheme } from "theme";
import { isNative, getPlatform } from "utils/platform.utils";
import useDeviceInfo from "hooks/useDeviceInfo.hook";
import useNetworkStatus from "hooks/useNetworkStatus.hook";
import useAppVersion from "hooks/useAppVersion.hook";
import SafeAreaViewComponent from "components/mobile/SafeAreaViewComponent";
import { StatusBar, Style } from "@capacitor/status-bar";

const AppHOC = ({ children }) => {

  // amCharts is only needed by lazy-loaded chart components. Registering the
  // license via a dynamic import keeps the whole library out of the entry chunk.
  useEffect(() => {
    import("@amcharts/amcharts5").then((am5) => {
      am5.addLicense(import.meta.env.VITE_AMCHARTS_LICENSE_KEY);
      am5.addLicense("AM5NOLOGO");
    });
  }, []);
  // States, refs, selectors, RTK Query hooks
  const { isDarkMode } = useTheme();
  const { deviceInfo } = useDeviceInfo();
  const { isOnline, connectionType } = useNetworkStatus();
  console.log("isNative", isNative());

  // const { pathname } = useLocation();

  const {
    version,
    build,
    appName,
    environment,
  } = useAppVersion();

  // Initialize Capacitor features
  useEffect(() => {
    const initializeCapacitor = async () => {
      if (isNative()) {
        console.log("Running on native platform:", getPlatform());

        // ── System Bars Configuration ─────────────────────────────────────────
        // Status bar  : black background, white icons/text (light content style)
        // Nav bar     : black background, light (white) icons — FIXED, never scrolls
        try {
          await StatusBar.setBackgroundColor({ color: "#000000" });
          await StatusBar.setStyle({ style: Style.Dark }); // Dark = white icons on black bg
          await StatusBar.setOverlaysWebView({ overlay: true }); // Overlay: let CSS env() handle insets
        } catch (e) {
          console.warn("StatusBar plugin error:", e);
        }

        if (deviceInfo) {
          console.log("Device info:", {
            deviceId: deviceInfo.deviceId,
            platform: deviceInfo.platform,
            model: deviceInfo.model,
            manufacturer: deviceInfo.manufacturer,
            operatingSystem: deviceInfo.operatingSystem,
            osVersion: deviceInfo.osVersion,
            isVirtual: deviceInfo.isVirtual,
          });
        }

        if (version) {
          console.log("App version info:", {
            version,
            build,
            appName,
            environment,
          });
        }

        console.log("Network status:", {
          isOnline,
          connectionType,
        });

        // Initialize other Capacitor plugins here if needed
        // Example: Push notifications, biometric auth, etc.
      } else {
        console.log("Running on web platform");

        if (deviceInfo) {
          console.log("Web device info:", {
            deviceId: deviceInfo.deviceId,
            platform: deviceInfo.platform,
            browserName: deviceInfo.browserName,
            browserVersion: deviceInfo.browserVersion,
            operatingSystem: deviceInfo.operatingSystem,
            osVersion: deviceInfo.osVersion,
            screenWidth: deviceInfo.screenWidth,
            screenHeight: deviceInfo.screenHeight,
            language: deviceInfo.language,
            timezone: deviceInfo.timezone,
          });
        }

        if (version) {
          console.log("Web app version info:", {
            version,
            build,
            appName,
            environment,
          });
        }

        console.log("Web network status:", {
          isOnline,
          connectionType,
        });
      }
    };

    initializeCapacitor();
  }, [deviceInfo, version, isOnline, connectionType, build, appName, environment]);

  // useEffect(() => {
  //   window.scrollTo({
  //     top: 0,
  //     left: 0,
  //     behavior: "instant",
  //   });
  // }, [pathname]);

  // Other functions and logic
  const currentTheme = createDynamicTheme(isDarkMode);

  // Return/rendering
  return (
    <ThemeProvider theme={currentTheme}>
      <SafeAreaViewComponent>
        {children}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: isDarkMode ? "#1e293b" : "#fff",
              color: isDarkMode ? "#fff" : "#1e293b",
              fontSize: 16, // 👈 font size
              fontWeight: "600", // 👈 font weight
              borderRadius: 8, // optional: makes toast look cleaner
              padding: "12px 16px",
            },
            success: {
              iconTheme: {
                primary: "#10b981",
                secondary: "#fff",
              },
            },
            error: {
              iconTheme: {
                primary: "#ef4444",
                secondary: "#fff",
              },
            },
          }}
        />
      </SafeAreaViewComponent>
    </ThemeProvider>
  );
};

AppHOC.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AppHOC;
